// The `sync` interface: the daily sync. A sync is the chess.com import of
// the last 30 days (only the games not in the library yet) followed by the
// scan pass (only the games not scanned yet). It runs once a day, before
// the app shows any screen on the rail (the sync screen, plugin
// screen-sync, holds the user there meanwhile), and again whenever the
// Sync button is pressed. The importer (plugin library-import-chesscom)
// provides it; the sync screen and its gate read it.
export * from './day.ts';

export const SYNC_API = 'sync';

export type SyncPhase = 'idle' | 'importing' | 'done' | 'error';

/** Where the sync's import stands; the scan after it is the scan queue's (`scan`). */
export interface SyncState {
  phase: SyncPhase;
  /** No import has ever completed for this account: the long first sync, as against a day's. */
  first: boolean;
  /** The month being read, "October 2026", while importing. */
  month?: string;
  /** Games the import brought in, and games it found already in the library. */
  imported?: number;
  skipped?: number;
  /** A sentence for an import that found nothing to bring in. */
  note?: string;
  /** Why the import failed. */
  error?: string;
}

export interface SyncService {
  /**
   * Whether a sync is due now: an account is set, none completed today (the
   * local day), and none was skipped this session. Reads the importer's
   * bookkeeping from storage, so it is current after a reload.
   */
  due(): Promise<boolean>;
  /**
   * Runs the sync: the import, then the scan pass. Resolves when the import
   * is done or has failed; the scan goes on in the queue (`scan.progress`).
   * Nothing happens while one runs already, or without an account.
   */
  run(): Promise<void>;
  /** Lets the user through today without a sync; forgotten on the next load. */
  skip(): void;
  /**
   * Starts the account over: stops the scan, removes every game of the
   * account (and with them their scans) and the importer's bookkeeping, so
   * the next sync is the first again. The landing page's way in.
   */
  reset(): Promise<void>;
  state(): SyncState;
  /** Hears every change of the state. Returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
}
